// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  AdvertisementType,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import AdvertisementFields from '../../fields/AdvertisementFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Advertisement'>>;
  loading: boolean;
  resourceData: Array<$Model<'Regatta'>>;
};

const pageId = adminPages.adminAdvertisementsPage;

class AdminAdvertisementsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.setupSocket();
    this.getData();
    this.getResourceData();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionId !== null && isDiff(
      main.activeRegionId,
      prevProps.main.activeRegionId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionSeasonId === data.RegionSeasonId) {
          if (data.modelName === 'Advertisement') {
            this.getData();
          }

          if (data.modelName === 'Regatta') {
            this.getResourceData();
          }
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyAdminAdvertisement({
            RegionSeasonId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          const resourceData = await Services.Api.getResourceRegatta({
            RegionSeasonId,
          });

          await this.setStateAsync({
            resourceData,
          });
        }
      },
    });
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    if (main.activeRegionSeasonId !== null) {
      const newItem: $ModelUpsertParams<'Advertisement'> = {
        Content: '',
        Email: '',
        EndDate: null,
        Name: '',
        Phone: null,
        Public: true,
        RegattaId: null,
        RegionSeasonId: main.activeRegionSeasonId,
        Type: AdvertisementType.crewmember,
        Uid: 'new',
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            rightButtons={(
              <FormDialog
                fields={(
                  <AdvertisementFields
                    item={newItem}
                    resourceData={resourceData}
                  />
                )}
                id={'create-advertisement-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'Advertisement'>>({
                      ...newItem,
                      ...values,
                    });

                    await Services.Api.upsertAdminAdvertisement(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Advertisement')}
              >
                <Button
                  color={'info'}
                  id={'create-advertisement-button'}
                  size={'sm'}
                  title={t('Add new advertisement')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Advertisements')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'Advertisement'>) {
    const {
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <AdvertisementFields
              item={dataItem}
              resourceData={resourceData}
            />
          )}
          id={`update-advertisement-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Advertisement'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminAdvertisement(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Advertisement')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-advertisement-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-advertisement-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminAdvertisement(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Advertisement')}
        >
          <Button
            color={'danger'}
            id={`delete-advertisement-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Type')}</th>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th>{t('Expires')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Advertisement'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Type}</td>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <TdDate
                countdown={t('Expired')}
                value={dataItem.EndDate}
              />
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminAdvertisementsPageView;
